import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { User, Phone, Building, Wrench, Lock, Save, ArrowLeft } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const ProfilePage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [user, setUser] = useState(null);
  
  // Profile form
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [company, setCompany] = useState('');
  const [specialization, setSpecialization] = useState('');
  
  // Password form
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/profile/me`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Failed to load profile');

      const data = await response.json();
      setUser(data);
      setEmail(data.email || '');
      setName(data.name || '');
      setPhone(data.phone || '');
      setCompany(data.company || '');
      setSpecialization(data.specialization || '');
    } catch (error) {
      console.error('Error loading profile:', error);
      toast({
        title: "Erreur",
        description: "Impossible de charger le profil",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const token = localStorage.getItem('token');
      const updateData = {
        email,
        name,
        phone,
        ...(user.role === 'client' && { company }),
        ...(user.role === 'engineer' && { specialization })
      };

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/profile/me`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updateData)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to update profile');
      }

      const result = await response.json();

      // Si l'email a changé, déconnecter l'utilisateur
      if (result.email_changed) {
        toast({
          title: "Email modifié",
          description: "Votre email a été modifié. Veuillez vous reconnecter avec votre nouvel email."
        });
        
        // Déconnexion après 2 secondes
        setTimeout(() => {
          localStorage.clear();
          navigate('/login');
        }, 2000);
      } else {
        toast({
          title: "Succès",
          description: "Profil mis à jour avec succès"
        });

        await loadProfile();
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      toast({
        title: "Erreur",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      toast({
        title: "Erreur",
        description: "Les mots de passe ne correspondent pas",
        variant: "destructive"
      });
      return;
    }

    if (newPassword.length < 8) {
      toast({
        title: "Erreur",
        description: "Le mot de passe doit contenir au moins 8 caractères",
        variant: "destructive"
      });
      return;
    }

    setSaving(true);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/profile/change-password`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to change password');
      }

      toast({
        title: "Succès",
        description: "Mot de passe changé avec succès"
      });

      // Reset password fields
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      console.error('Error changing password:', error);
      toast({
        title: "Erreur",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#111111] flex items-center justify-center">
        <p className="text-white">Chargement...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#111111] p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Button
            onClick={() => navigate(-1)}
            variant="outline"
            className="mb-4 border-[#A6A6A6] text-white hover:bg-[#A6A6A6]/10"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Retour
          </Button>
          
          <h1 className="text-4xl font-bold">
            <span className="text-white">Mon</span>{' '}
            <span className="text-[#C00000]">Compte</span>
          </h1>
          <p className="text-[#A6A6A6] mt-2">Gérez vos informations personnelles</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Informations du profil */}
          <Card className="bg-[#1a1a1a] border-[#A6A6A6]/20">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <User className="h-5 w-5 text-[#C00000]" />
                Informations du profil
              </CardTitle>
              <CardDescription className="text-[#A6A6A6]">
                Modifiez vos informations personnelles
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleUpdateProfile} className="space-y-4">
                <div>
                  <Label htmlFor="email" className="text-[#A6A6A6]">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="bg-[#111111] border-[#A6A6A6]/30 text-white focus:border-[#C00000]"
                  />
                  <p className="text-xs text-[#A6A6A6] mt-1">⚠️ Modifier l'email vous déconnectera</p>
                </div>

                <div>
                  <Label htmlFor="name" className="text-[#A6A6A6]">Nom complet *</Label>
                  <Input
                    id="name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    className="bg-[#111111] border-[#A6A6A6]/30 text-white focus:border-[#C00000]"
                  />
                </div>

                <div>
                  <Label htmlFor="phone" className="text-[#A6A6A6]">Téléphone *</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    required
                    className="bg-[#111111] border-[#A6A6A6]/30 text-white focus:border-[#C00000]"
                  />
                </div>

                {user?.role === 'client' && (
                  <div>
                    <Label htmlFor="company" className="text-[#A6A6A6]">Entreprise</Label>
                    <div className="relative">
                      <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-[#A6A6A6]" />
                      <Input
                        id="company"
                        type="text"
                        value={company}
                        onChange={(e) => setCompany(e.target.value)}
                        className="pl-10 bg-[#111111] border-[#A6A6A6]/30 text-white focus:border-[#C00000]"
                      />
                    </div>
                  </div>
                )}

                {user?.role === 'engineer' && (
                  <div>
                    <Label htmlFor="specialization" className="text-[#A6A6A6]">Spécialisation</Label>
                    <div className="relative">
                      <Wrench className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-[#A6A6A6]" />
                      <Input
                        id="specialization"
                        type="text"
                        value={specialization}
                        onChange={(e) => setSpecialization(e.target.value)}
                        className="pl-10 bg-[#111111] border-[#A6A6A6]/30 text-white focus:border-[#C00000]"
                      />
                    </div>
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={saving}
                  className="w-full bg-[#C00000] hover:bg-[#C00000]/90 text-white"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {saving ? 'Enregistrement...' : 'Enregistrer les modifications'}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Changement de mot de passe */}
          <Card className="bg-[#1a1a1a] border-[#A6A6A6]/20">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Lock className="h-5 w-5 text-[#C00000]" />
                Changer le mot de passe
              </CardTitle>
              <CardDescription className="text-[#A6A6A6]">
                Mettez à jour votre mot de passe
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleChangePassword} className="space-y-4">
                <div>
                  <Label htmlFor="currentPassword" className="text-[#A6A6A6]">Mot de passe actuel *</Label>
                  <Input
                    id="currentPassword"
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    required
                    className="bg-[#111111] border-[#A6A6A6]/30 text-white focus:border-[#C00000]"
                  />
                </div>

                <div>
                  <Label htmlFor="newPassword" className="text-[#A6A6A6]">Nouveau mot de passe *</Label>
                  <Input
                    id="newPassword"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={8}
                    className="bg-[#111111] border-[#A6A6A6]/30 text-white focus:border-[#C00000]"
                  />
                  <p className="text-xs text-[#A6A6A6] mt-1">Minimum 8 caractères</p>
                </div>

                <div>
                  <Label htmlFor="confirmPassword" className="text-[#A6A6A6]">Confirmer le mot de passe *</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    minLength={8}
                    className="bg-[#111111] border-[#A6A6A6]/30 text-white focus:border-[#C00000]"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={saving}
                  className="w-full bg-[#C00000] hover:bg-[#C00000]/90 text-white"
                >
                  <Lock className="h-4 w-4 mr-2" />
                  {saving ? 'Changement...' : 'Changer le mot de passe'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
